/**
 * The 50 US states plus the District of Columbia — one list, shared by the marketing
 * signup form and the gym-admin signup route.
 *
 * WHY THIS IS SHARED: same reason as `@cheervue/signup-country`. The form decides what
 * a gym owner may pick and the route decides what it will accept. Two copies of that
 * answer drift silently and in the worst direction — a form offering a value the route
 * rejects, or a route accepting one the form would never have shown.
 *
 * WHY IT EXISTS: signup asked for "City & State" as one free-text box with the
 * placeholder "Chantilly, VA". Owners answered it however they liked, so the platform
 * signup queue shows "Turlock, CA" next to "Madison, MS" next to "LAPORTE CITY iowa",
 * and searching the queue by state finds whichever spelling the owner happened to use.
 *
 * NOT A COUNTRY LIST. Puerto Rico, Guam, the US Virgin Islands, American Samoa and the
 * Northern Mariana Islands are deliberately absent: `@cheervue/signup-country` asks
 * which country the gym is in, and each of those is its own answer there — declined,
 * since 2026-09-11, like every answer that is not the United States. Repeating them
 * here would be a second place to answer the same question, and the two answers could
 * disagree.
 */

export interface UsState {
  /** USPS two-letter code. Stable — this is the stored value. */
  value: string;
  /** What the gym owner reads in the dropdown. Safe to reword. */
  label: string;
}

/**
 * Alphabetical by label, which is how a 51-item dropdown is scanned. DC sorts under
 * "D" rather than being pinned to the end: someone looking for it looks under D.
 */
export const US_STATES: readonly UsState[] = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
];

/** Lookup set built from the list, so the two can never disagree. */
const US_STATE_CODES = new Set(US_STATES.map((state) => state.value));

/**
 * Case-sensitive on purpose: the value comes from a `<select>` whose options this same
 * list generated, so anything else is a caller that built its own value. Accepting
 * "va" here would let a hand-rolled client write a code the dropdown cannot round-trip.
 *
 * Returns a plain boolean rather than a `value is string` type predicate. As a predicate
 * it read better but broke its only real caller: the signup route tests an already-typed
 * `string`, so `!isUsStateCode(typedState)` narrowed the false branch to `never` and the
 * next line's `typedState.slice(...)` stopped type-checking. No caller uses the narrowing
 * — every one of them passes a value it has already established is a string, or discards
 * the result — so the predicate was pure cost.
 */
export function isUsStateCode(value: unknown): boolean {
  return typeof value === 'string' && US_STATE_CODES.has(value);
}

/**
 * The single format the signup queue stores and displays: "Chantilly, VA".
 *
 * `region` is the state code a US gym picked — the thing a person writes after the city.
 * It took a territory code (PR, GU, VI, AS, MP) too until 2026-09-11, when the
 * territories stopped being served. `@cheervue/signup-country` is the authority on which
 * answers get this far: the United States, and nothing else.
 */
export function formatCityAndRegion(city: string, region: string): string {
  const trimmedRegion = region.trim();
  let trimmedCity = city.trim();

  // The field this replaces was labelled "City & State" with the placeholder
  // "Chantilly, VA", so typing the state into the city box is the habit we trained.
  // Drop it only when it repeats the region they ALSO picked — "Chantilly, VA" + VA
  // is unambiguous. "Kansas City, KS" + MO is not, and a guess there would silently
  // relocate a gym, so that one is left exactly as typed for a human to read.
  //
  // The two forms are stripped under DIFFERENT rules, and the difference is load-bearing:
  //
  //  - The two-letter CODE may be separated by a comma or by a space. Checked against
  //    18,720 distinct US city names across all 51 state codes and the 5 territory
  //    codes — 1,048,320 combinations — this never fires on a real city name, because
  //    the code has to be the whole final token.
  //  - The full NAME is stripped only after a COMMA. "Port Washington" + WA and
  //    "West New York" + NY are real cities whose last word is a state name, and a
  //    space-separated rule would file them as "Port, WA" and "West, NY". A comma is
  //    the unambiguous "City, State" form a person writes on purpose.
  //
  // So "Palmer, Alaska" collapses and "LAPORTE CITY iowa" does not — the latter keeps
  // what was typed and reads a little oddly, which is the safe direction.
  if (trimmedRegion) {
    const escaped = trimmedRegion.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    trimmedCity = trimmedCity.replace(new RegExp(`[,\\s]+${escaped}\\.?$`, 'i'), '').trim();

    const regionName = US_STATES.find((state) => state.value === trimmedRegion)?.label;
    if (regionName) {
      const escapedName = regionName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      trimmedCity = trimmedCity.replace(new RegExp(`,\\s*${escapedName}\\.?$`, 'i'), '').trim();
    }
  }

  if (!trimmedRegion) return trimmedCity;
  if (!trimmedCity) return trimmedRegion;
  return `${trimmedCity}, ${trimmedRegion}`;
}
